import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Tabs,
  TabsHeader,
  Tab,
  Progress,
} from '@material-tailwind/react';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AgGridReact } from 'ag-grid-react';
import Select from 'react-select';
import { writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import db from '../../firebase';
import Papa from 'papaparse';
import { UserAuth } from '../../context/AuthContext';
import { getWebStats, getWebStatsListingDetail, rebuildWebStatsCumulative } from '../../fb-functions';
import WebStatsListingDialog from '../../components/WebStatsListingDialog';
import WebStatsCharts from '../../components/WebStatsCharts';

// ─── CSV field mapping (all 33 Controller CSV fields) ───────────────────────
const ROW_MAP = {
  ListingID:                      { field: 'listing_id',                       type: 'number' },
  Industry:                       { field: 'industry',                         type: 'string' },
  Year:                           { field: 'year',                             type: 'number' },
  Manufacturer:                   { field: 'manufacturer',                     type: 'string' },
  Model:                          { field: 'model',                            type: 'string' },
  Category:                       { field: 'category',                         type: 'string' },
  ListingTitle:                   { field: 'listing_title',                    type: 'string' },
  Status:                         { field: 'status',                           type: 'string' },
  SerialNumber:                   { field: 'serial_number',                    type: 'string' },
  StockNumber:                    { field: 'stock_number',                     type: 'string' },
  LotNumberViews:                 { field: 'lot_number_views',                 type: 'number' },
  QuickFindViews:                 { field: 'quick_find_views',                 type: 'number' },
  DetailsViews:                   { field: 'details_views',                    type: 'number' },
  DailyDetailViews:               { field: 'daily_detail_views',               type: 'number' },
  Impressions:                    { field: 'impressions',                      type: 'number' },
  DailyImpressions:               { field: 'daily_impressions',                type: 'number' },
  ClickThroughRate:               { field: 'click_through_rate',               type: 'number' },
  TotalClicksToCall:              { field: 'total_clicks_to_call',             type: 'number' },
  TotalClicksToText:              { field: 'total_clicks_to_text',             type: 'number' },
  TotalWhatsAppClicks:            { field: 'total_whatsapp_clicks',            type: 'number' },
  TotalFacebookMessagingClicks:   { field: 'total_facebook_messaging_clicks',  type: 'number' },
  AutomaticWatchlistAdds:         { field: 'automatic_watchlist_adds',         type: 'number' },
  ManualWatchlistAdds:            { field: 'manual_watchlist_adds',            type: 'number' },
  TotalWatchlists:                { field: 'total_watchlists',                 type: 'number' },
  EmailLeads:                     { field: 'email_leads',                      type: 'number' },
  DaysAging:                      { field: 'days_aging',                       type: 'number' },
  ListingURL:                     { field: 'listing_url',                      type: 'string' },
  IsFeatured:                     { field: 'is_featured',                      type: 'string' },
  FeaturedDates:                  { field: 'featured_dates',                   type: 'string' },
  AdType:                         { field: 'ad_type',                          type: 'string' },
  Condition:                      { field: 'condition',                        type: 'string' },
  AccountName:                    { field: 'account_name',                     type: 'string' },
  MachineLocation:                { field: 'machine_location',                 type: 'string' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const NUMBER_FMT = new Intl.NumberFormat('en-US');
const formatNum = (v) => (v != null && !isNaN(v) ? NUMBER_FMT.format(Math.round(v)) : '');
const formatPct = (v) => (v != null && !isNaN(v) ? `${(v * 100).toFixed(2)}%` : '');
const formatDec = (v) => (v != null && !isNaN(v) ? v.toFixed(1) : '');
const formatCurrency = (v) =>
  v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v) : '';

const formatDate = (val) => {
  if (!val) return '';
  const d = val.toDate ? val.toDate() : val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

// Extract date from Controller CSV filename: "...-MM_DD_YYYY ..." or "...-M_DD_YYYY ..."
const extractFileDt = (filename) => {
  try {
    const match = filename.match(/(\d{1,2}_\d{1,2}_\d{4})/);
    if (match) return match[1];
    // Fallback: try splitting on dash
    const part = filename.split('-')[1];
    if (part) return part.split(' ')[0].trim();
  } catch {}
  return null;
};

const parseFileDt = (raw) => {
  if (!raw) return null;
  const [mm, dd, yyyy] = raw.split('_').map(Number);
  return new Date(yyyy, mm - 1, dd);
};

const processCSVRows = (rows, fileDt) => {
  const result = [];
  for (const row of rows) {
    const mapped = {};
    let hasListingId = false;
    for (const [csvKey, val] of Object.entries(row)) {
      const mapping = ROW_MAP[csvKey];
      if (!mapping) continue;
      let v = (val || '').trim();
      if (mapping.type === 'number') {
        const hadPercent = v.includes('%');
        v = parseFloat(v.replace(/[$,%]/g, ''));
        if (isNaN(v)) v = 0;
        if (hadPercent && mapping.field === 'click_through_rate') {
          v = v / 100;
        }
      }
      // Clean =HYPERLINK("url") format from Controller CSVs
      if (mapping.field === 'listing_url') {
        const hMatch = v.match(/HYPERLINK\("([^"]*)"\)/);
        if (hMatch) v = hMatch[1];
        // Drop anything that isn't a real absolute URL (e.g. empty hyperlinks)
        if (!/^https?:\/\//i.test(v.trim())) v = '';
      }
      mapped[mapping.field] = v;
      if (mapping.field === 'listing_id' && v) hasListingId = true;
    }
    if (!hasListingId) continue;
    mapped.fileDt = fileDt;
    result.push(mapped);
  }
  return result;
};

// ─── react-select styling ────────────────────────────────────────────────────
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '36px',
    fontSize: '13px',
    borderColor: state.isFocused ? '#3b82f6' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    '&:hover': { borderColor: '#3b82f6' },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#eff6ff',
    borderRadius: '9999px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#1d4ed8',
    fontSize: '11px',
    fontWeight: 500,
    padding: '1px 6px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#93c5fd',
    borderRadius: '0 9999px 9999px 0',
    '&:hover': { backgroundColor: '#dbeafe', color: '#1d4ed8' },
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '13px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
    padding: '6px 12px',
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ─── Main Component ──────────────────────────────────────────────────────────
function WebStatsPage() {
  const { isAdmin, webStatsAdmin, user } = UserAuth();
  const [listings, setListings] = useState([]);
  const [modelBenchmarks, setModelBenchmarks] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live');
  const [viewMode, setViewMode] = useState('cumulative'); // 'weekly' | 'cumulative'
  const [fileDtOptions, setFileDtOptions] = useState([]);
  const [selectedFileDt, setSelectedFileDt] = useState(null);

  const [lastUpload, setLastUpload] = useState(null);

  // Quick filters
  const [filterContact, setFilterContact] = useState([]);
  const [filterManufacturer, setFilterManufacturer] = useState([]);
  const [filterModel, setFilterModel] = useState([]);
  const [groupBy, setGroupBy] = useState('none');

  // Detail dialog state
  const [selectedListing, setSelectedListing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Upload state
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' });
  const [uploadLog, setUploadLog] = useState([]);
  const fileInputRef = useRef(null);

  // ─── Data loading via Cloud Function ─────────────────────────────────────
  const loadData = useCallback(async (mode, fileDt) => {
    try {
      setLoading(true);
      const result = await getWebStats({ mode: mode || viewMode, fileDt });
      if (!result.success) {
        console.error('Failed to load web stats:', result.error);
        return;
      }
      const { listings: rows, fileDtOptions: dtOpts, selectedFileDt: selDt, modelBenchmarks: bm, lastUpload: lu } = result.data;

      const enriched = (rows || []).map((d, i) => ({
        ...d,
        _id: d._id || `row_${d.listing_id}_${i}`,
      }));
      setListings(enriched);
      setModelBenchmarks(bm || {});

      const opts = (dtOpts || []).map((o) => ({ raw: o.raw, dt: new Date(o.ts) }));
      setFileDtOptions(opts);
      if (selDt && !fileDt) setSelectedFileDt(selDt);
      setLastUpload(lu || null);
    } catch (err) {
      console.error('Failed to load web stats data:', err);
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  // Initial load
  useEffect(() => { loadData(viewMode, selectedFileDt); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload when view mode or selected week changes
  const handleViewModeChange = useCallback((newMode) => {
    setViewMode(newMode);
    loadData(newMode, newMode === 'weekly' ? selectedFileDt : null);
  }, [loadData, selectedFileDt]);

  const handleFileDtChange = useCallback((newDt) => {
    setSelectedFileDt(newDt);
    loadData('weekly', newDt);
  }, [loadData]);

  // Listings come pre-enriched from the Cloud Function
  const activeListings = listings;
  const defaultContactApplied = useRef(false);

  // ─── Filter options (built from full enriched dataset) ──────────────────
  const filterOptions = useMemo(() => {
    const contacts = new Set();
    const manufacturers = new Set();
    const models = new Set();
    activeListings.forEach((d) => {
      if (d.contact_name) contacts.add(d.contact_name);
      if (d.manufacturer) manufacturers.add(d.manufacturer);
      if (d.model) models.add(d.model);
    });
    const toOpts = (s) => [...s].sort().map((v) => ({ value: v, label: v }));
    return {
      contacts: toOpts(contacts),
      manufacturers: toOpts(manufacturers),
      models: toOpts(models),
    };
  }, [activeListings]);

  // Auto-select logged-in user as default contact filter on first load
  // Uses fuzzy matching (>90% similarity) to handle minor name differences
  useEffect(() => {
    if (defaultContactApplied.current || !user?.displayName || !filterOptions.contacts.length) return;
    const userName = user.displayName.toLowerCase().trim();
    // Simple similarity: ratio of matching characters (bigram overlap)
    const similarity = (a, b) => {
      if (!a || !b) return 0;
      const bigrams = (s) => { const r = []; for (let i = 0; i < s.length - 1; i++) r.push(s.slice(i, i + 2)); return r; };
      const aBi = bigrams(a.toLowerCase());
      const bBi = bigrams(b.toLowerCase());
      const intersection = aBi.filter((bg) => bBi.includes(bg)).length;
      return (2 * intersection) / (aBi.length + bBi.length);
    };
    let bestMatch = null;
    let bestScore = 0;
    for (const opt of filterOptions.contacts) {
      const score = similarity(userName, opt.value);
      if (score > bestScore) { bestScore = score; bestMatch = opt; }
    }
    if (bestMatch && bestScore >= 0.9) {
      setFilterContact([bestMatch]);
      defaultContactApplied.current = true;
    }
  }, [filterOptions.contacts, user]);

  // ─── Apply quick filters ──────────────────────────────────────────────────
  const filteredListings = useMemo(() => {
    let data = activeListings;
    if (filterContact.length > 0) {
      const vals = new Set(filterContact.map((o) => o.value));
      data = data.filter((d) => vals.has(d.contact_name));
    }
    if (filterManufacturer.length > 0) {
      const vals = new Set(filterManufacturer.map((o) => o.value));
      data = data.filter((d) => vals.has(d.manufacturer));
    }
    if (filterModel.length > 0) {
      const vals = new Set(filterModel.map((o) => o.value));
      data = data.filter((d) => vals.has(d.model));
    }
    return data;
  }, [activeListings, filterContact, filterManufacturer, filterModel]);

  const hasActiveFilters = filterContact.length > 0 || filterManufacturer.length > 0 || filterModel.length > 0;

  const liveListings = useMemo(
    () => filteredListings.filter((d) => (d.status || '').toUpperCase() === 'ACTIVE'),
    [filteredListings]
  );
  const soldListings = useMemo(
    () => filteredListings.filter((d) => (d.status || '').toUpperCase() !== 'ACTIVE'),
    [filteredListings]
  );

  // ─── Summary stats ───────────────────────────────────────────────────────
  const summaryStats = useMemo(() => {
    const data =
      activeTab === 'live' ? liveListings :
      activeTab === 'sold' ? soldListings :
      filteredListings;
    if (!data.length) return null;
    return {
      count: data.length,
      totalViews: data.reduce((s, d) => s + (d.details_views || 0), 0),
      totalImpressions: data.reduce((s, d) => s + (d.impressions || 0), 0),
      avgDailyViews: data.reduce((s, d) => s + (d.daily_detail_views || 0), 0) / data.length,
      avgCTR: data.reduce((s, d) => s + (d.click_through_rate || 0), 0) / data.length,
      totalWatchlists: data.reduce((s, d) => s + (d.total_watchlists || 0), 0),
      totalLeads: data.reduce((s, d) => s + (d.email_leads || 0), 0),
    };
  }, [activeTab, liveListings, soldListings, filteredListings]);

  // ─── Upload logic ────────────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const queue = [];
    for (const file of files) {
      const fileDt = extractFileDt(file.name);
      if (!fileDt) {
        queue.push({ name: file.name, error: 'Could not extract date from filename', rows: [] });
        continue;
      }
      // Check for duplicate
      const existing = fileDtOptions.some((o) => o.raw === fileDt);
      const parsed = await new Promise((resolve) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => resolve(results.data),
        });
      });
      const processed = processCSVRows(parsed, fileDt);
      queue.push({
        name: file.name,
        fileDt,
        rows: processed,
        count: processed.length,
        existing,
      });
    }
    setUploadQueue(queue);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  const handleUploadClick = () => {
    const hasExisting = uploadQueue.some((f) => f.existing && f.rows.length > 0 && !f.error);
    if (hasExisting) {
      setShowOverwriteConfirm(true);
    } else {
      executeUpload();
    }
  };

  const executeUpload = async () => {
    setShowOverwriteConfirm(false);
    const toUpload = uploadQueue.filter((f) => f.rows.length > 0 && !f.error);
    if (!toUpload.length) return;

    setUploading(true);
    setUploadLog([]);
    const totalFiles = toUpload.length;
    let completedFiles = 0;

    for (const file of toUpload) {
      setUploadProgress({ current: completedFiles, total: totalFiles, currentFile: file.name });
      try {
        // Firestore batch limit is 500; split into chunks
        const rows = file.rows;
        const BATCH_SIZE = 450;
        for (let i = 0; i < rows.length; i += BATCH_SIZE) {
          const chunk = rows.slice(i, i + BATCH_SIZE);
          const batch = writeBatch(db);
          for (const row of chunk) {
            // Deterministic doc ID: listingId_fileDt
            const docId = `${row.listing_id}_${row.fileDt}`;
            const ref = doc(db, 'webstats', docId);
            batch.set(ref, { ...row, created: serverTimestamp() });
          }
          await batch.commit();
        }
        setUploadLog((prev) => [...prev, { name: file.name, status: 'success', count: rows.length }]);
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
        setUploadLog((prev) => [...prev, { name: file.name, status: 'error', error: err.message }]);
      }
      completedFiles++;
    }

    setUploadProgress({ current: totalFiles, total: totalFiles, currentFile: '' });
    setUploading(false);
    setUploadQueue([]);
    // Reload data + rebuild cumulative cache in background
    await loadData(viewMode, selectedFileDt);
    rebuildWebStatsCumulative().then((r) => {
      if (r.success) console.log('Cumulative cache rebuilt:', r.data);
    });
  };

  // ─── Column definitions ──────────────────────────────────────────────────
  const isCumulative = viewMode === 'cumulative';

  const colDefs = useMemo(() => [
    {
      field: 'listing_id',
      headerName: 'Listing ID',
      width: 130,
      pinned: 'left',
      cellRenderer: (p) => {
        const raw = typeof p.data.listing_url === 'string' ? p.data.listing_url.trim() : '';
        // Use the stored URL when valid, otherwise derive the Controller listing URL from the ID.
        const url = /^https?:\/\//i.test(raw)
          ? raw
          : (p.value ? `https://www.controller.com/listing/for-sale/${p.value}` : '');
        return url
          ? <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 visited:text-blue-600 hover:underline">{p.value}</a>
          : p.value;
      },
    },
    { field: 'reg', headerName: 'Tail #', width: 100,
      cellStyle: (p) => p.value ? {} : { color: '#d1d5db' },
      valueFormatter: (p) => p.value || '—',
    },
    { field: 'contact_name', headerName: 'Contact', width: 130,
      cellStyle: (p) => p.value ? {} : { color: '#d1d5db' },
      valueFormatter: (p) => p.value || '—',
    },
    { field: 'year', headerName: 'Year', width: 75 },
    { field: 'manufacturer', headerName: 'Manufacturer', width: 120 },
    { field: 'model', headerName: 'Model', width: 150 },
    { field: 'category', headerName: 'Category', width: 160 },
    { field: 'status', headerName: 'Status', width: 100,
      cellStyle: (p) => {
        const v = (p.value || '').toUpperCase();
        if (v === 'ACTIVE') return { color: '#059669', fontWeight: 600 };
        if (v === 'SOLD') return { color: '#dc2626', fontWeight: 600 };
        return { color: '#6b7280' };
      },
    },
    { field: 'serial_number', headerName: 'Serial #', width: 110 },
    { field: 'listing_date', headerName: 'Listing Date', width: 115, valueFormatter: (p) => formatDate(p.value) },
    { field: 'sold_date', headerName: 'Sold Date', width: 115,
      valueFormatter: (p) => formatDate(p.value),
      cellStyle: (p) => p.value ? { color: '#dc2626' } : {},
    },
    { field: 'dom', headerName: 'DOM', width: 75, type: 'numericColumn' },
    { field: 'days_aging', headerName: 'True DOM', width: 100, type: 'numericColumn',
      valueGetter: (p) => p.data.relist_true_dom || p.data.days_aging,
      cellStyle: (p) => p.data.relist_count > 1 ? { fontWeight: 600 } : {},
    },
    ...(isCumulative ? [{
      headerName: 'Relist',
      width: 65,
      valueGetter: (p) => p.data.relist_count > 1 ? p.data.relist_count : null,
      valueFormatter: (p) => p.value ? `${p.value}x` : '',
      cellStyle: (p) => p.value ? { color: '#d97706', fontWeight: 600 } : {},
      type: 'numericColumn',
    }] : []),
    { headerName: 'vs Avg DOM', width: 110,
      valueGetter: (p) => {
        const model = (p.data.model || '').toUpperCase().trim();
        const bm = modelBenchmarks[model];
        const dom = p.data.relist_true_dom || p.data.days_aging;
        if (!bm || !dom) return null;
        return dom - bm.avgDom;
      },
      valueFormatter: (p) => {
        if (p.value == null) return '—';
        const sign = p.value > 0 ? '+' : '';
        return `${sign}${p.value}d`;
      },
      cellStyle: (p) => {
        if (p.value == null) return { color: '#d1d5db' };
        if (p.value > 30) return { color: '#dc2626', fontWeight: 600 };
        if (p.value > 0) return { color: '#f59e0b', fontWeight: 500 };
        return { color: '#059669' };
      },
      type: 'numericColumn',
    },
    ...(isCumulative ? [{ field: 'weeks_tracked', headerName: 'Weeks', width: 75, type: 'numericColumn',
      valueGetter: (p) => p.data.relist_count > 1 ? p.data.relist_cycle_weeks : p.data.weeks_tracked,
    }] : []),
    { field: 'details_views', headerName: isCumulative ? 'Total Views' : 'Detail Views', width: 110, type: 'numericColumn',
      valueGetter: (p) => isCumulative && p.data.relist_count > 1 ? p.data.relist_cycle_views : p.data.details_views,
      valueFormatter: (p) => formatNum(p.value),
    },
    { field: 'daily_detail_views', headerName: isCumulative ? 'Avg Daily Views' : 'Daily Views', width: 115, valueFormatter: (p) => formatDec(p.value), type: 'numericColumn' },
    { field: 'impressions', headerName: isCumulative ? 'Total Impr.' : 'Impressions', width: 115, type: 'numericColumn',
      valueGetter: (p) => isCumulative && p.data.relist_count > 1 ? p.data.relist_cycle_impressions : p.data.impressions,
      valueFormatter: (p) => formatNum(p.value),
    },
    { field: 'daily_impressions', headerName: isCumulative ? 'Avg Daily Impr.' : 'Daily Impr.', width: 115, valueFormatter: (p) => formatDec(p.value), type: 'numericColumn' },
    { field: 'click_through_rate', headerName: isCumulative ? 'Avg CTR' : 'CTR', width: 80, valueFormatter: (p) => formatPct(p.value), type: 'numericColumn' },
    { field: 'total_watchlists', headerName: 'Watchlists', width: 100, valueFormatter: (p) => formatNum(p.value), type: 'numericColumn' },
    { field: 'email_leads', headerName: 'Email Leads', width: 100, type: 'numericColumn' },
    { field: 'total_clicks_to_call', headerName: 'Clicks to Call', width: 110, type: 'numericColumn' },
    { field: 'sale_price_usd', headerName: 'Sale Price', width: 115, valueFormatter: (p) => formatCurrency(p.value), type: 'numericColumn' },
    { field: 'sale_broker', headerName: 'Broker', width: 120 },
    { field: 'condition', headerName: 'Condition', width: 90 },
    { field: 'account_name', headerName: 'Account', width: 150 },
    { field: 'machine_location', headerName: 'Location', width: 160 },
    { field: 'is_featured', headerName: 'Featured', width: 90 },
    { field: 'ad_type', headerName: 'Ad Type', width: 80 },
  ], [isCumulative, modelBenchmarks]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    suppressMovable: true,
  }), []);

  // Build row data with optional group header rows
  const getRowData = useCallback(() => {
    let base;
    if (activeTab === 'live') base = liveListings;
    else if (activeTab === 'sold') base = soldListings;
    else base = filteredListings;

    if (groupBy === 'none') return base;

    // Group by the selected field
    const fieldMap = {
      category: 'category',
      manufacturer: 'manufacturer',
      model: 'model',
      contact: 'contact_name',
    };
    const field = fieldMap[groupBy];
    if (!field) return base;

    // Build groups
    const groups = {};
    base.forEach((row) => {
      const key = row[field] || 'Unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    // Sort groups by name, then flatten with header rows
    const result = [];
    const sortedKeys = Object.keys(groups).sort();
    sortedKeys.forEach((key) => {
      const items = groups[key];
      const totalViews = items.reduce((s, d) => s + (d.details_views || 0), 0);
      const totalImpressions = items.reduce((s, d) => s + (d.impressions || 0), 0);
      // Insert a synthetic group header row
      result.push({
        _id: `__group__${key}`,
        _isGroupHeader: true,
        _groupLabel: key,
        _groupCount: items.length,
        _groupViews: totalViews,
        _groupImpressions: totalImpressions,
      });
      // Sort items within group by detail views descending
      items.sort((a, b) => (b.details_views || 0) - (a.details_views || 0));
      result.push(...items);
    });
    return result;
  }, [activeTab, liveListings, soldListings, filteredListings, groupBy]);

  // Load weekly data for a single listing (for the detail dialog)
  const loadWeeklyData = useCallback(async (listingId) => {
    const result = await getWebStatsListingDetail({ listingId });
    if (result.success) return result.data.weeks || [];
    return [];
  }, []);

  // Row click → open detail dialog
  const onRowClicked = useCallback((params) => {
    if (params.data?._isGroupHeader) return;
    // Clicks on the listing-id link should only follow the link, not open the dialog.
    const target = params.event?.target;
    if (target?.closest?.('a')) return;
    setSelectedListing(params.data);
    setDialogOpen(true);
  }, []);

  // Peer listings for the selected listing (same model in current view)
  const peerListings = useMemo(() => {
    if (!selectedListing) return [];
    return activeListings.filter((d) => d.model === selectedListing.model);
  }, [selectedListing, activeListings]);

  const isFullWidthRow = useCallback((params) => {
    return params.rowNode.data?._isGroupHeader === true;
  }, []);

  const fullWidthCellRenderer = useCallback((params) => {
    const d = params.data;
    return (
      <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 font-semibold text-sm text-gray-700">
        <span>{d._groupLabel}</span>
        <span className="text-xs font-normal text-gray-400">
          {d._groupCount} listing{d._groupCount !== 1 ? 's' : ''}
        </span>
        <span className="text-xs font-normal text-gray-400">·</span>
        <span className="text-xs font-normal text-gray-500">
          {formatNum(d._groupViews)} views
        </span>
        <span className="text-xs font-normal text-gray-400">·</span>
        <span className="text-xs font-normal text-gray-500">
          {formatNum(d._groupImpressions)} impressions
        </span>
      </div>
    );
  }, []);

  // ─── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const regCount = activeListings.filter((d) => d.reg).length;
  const soldDateCount = activeListings.filter((d) => d.sold_date).length;

  return (
    <div className="p-6 h-[calc(100vh-100px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <Typography variant="h5" color="blue-gray">
            Web Stats
          </Typography>
          <div className="flex items-center gap-4">
            {isCumulative ? (
              <Typography variant="small" className="text-gray-500">
                Cumulative — {fileDtOptions.length} weeks ({fileDtOptions[fileDtOptions.length - 1]?.dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} – {fileDtOptions[0]?.dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})
              </Typography>
            ) : selectedFileDt && (
              <Typography variant="small" className="text-gray-500">
                Week of {fileDtOptions.find((o) => o.raw === selectedFileDt)?.dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || selectedFileDt}
              </Typography>
            )}
            {activeListings.length > 0 && (
              <Typography variant="small" className="text-gray-400">
                {regCount}/{activeListings.length} tail # matched · {soldDateCount} with sold date
              </Typography>
            )}
            {lastUpload && (
              <Typography variant="small" className="text-gray-400">
                Last upload: {new Date(lastUpload.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {lastUpload.fileDt && ` (file: ${parseFileDt(lastUpload.fileDt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || lastUpload.fileDt})`}
              </Typography>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* View mode toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => handleViewModeChange('weekly')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === 'weekly'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => handleViewModeChange('cumulative')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-300 ${
                viewMode === 'cumulative'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Cumulative
            </button>
          </div>
          {viewMode === 'weekly' && (
            <select
              value={selectedFileDt || ''}
              onChange={(e) => handleFileDtChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
            >
              {fileDtOptions.length === 0 && <option value="">No data uploaded</option>}
              {fileDtOptions.map((opt) => (
                <option key={opt.raw} value={opt.raw}>
                  {opt.dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </option>
            ))}
          </select>
          )}
          {(isAdmin || webStatsAdmin) && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                size="sm"
                variant="outlined"
                className="flex items-center gap-1"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                Upload CSVs
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Upload queue panel */}
      {(uploadQueue.length > 0 || uploading || uploadLog.length > 0) && (
        <Card className="mb-4 shadow-sm">
          <CardBody className="p-4">
            {/* Queued files */}
            {uploadQueue.length > 0 && !uploading && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <Typography variant="small" className="font-semibold text-gray-700">
                    {uploadQueue.length} file{uploadQueue.length > 1 ? 's' : ''} ready to upload
                  </Typography>
                  <div className="flex gap-2">
                    <Button variant="text" size="sm" onClick={() => setUploadQueue([])}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleUploadClick}
                      disabled={uploadQueue.every((f) => f.rows.length === 0 || f.error)}
                    >
                      Upload All
                    </Button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {uploadQueue.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                      <span className="text-gray-600 truncate max-w-[300px]">{f.name}</span>
                      <div className="flex items-center gap-2">
                        {f.existing && (
                          <span className="text-amber-600 text-[10px] font-medium bg-amber-50 px-1.5 py-0.5 rounded">
                            will overwrite
                          </span>
                        )}
                        {f.error ? (
                          <span className="text-red-500">{f.error}</span>
                        ) : (
                          <span className="text-gray-500">{f.count} rows · {f.fileDt}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Upload progress */}
            {uploading && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Spinner className="h-4 w-4" />
                  <Typography variant="small" className="text-gray-600">
                    Uploading {uploadProgress.current + 1} of {uploadProgress.total}: {uploadProgress.currentFile}
                  </Typography>
                </div>
                <Progress
                  value={uploadProgress.total > 0 ? ((uploadProgress.current / uploadProgress.total) * 100) : 0}
                  color="blue"
                  className="h-2"
                />
              </div>
            )}

            {/* Upload log */}
            {!uploading && uploadLog.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Typography variant="small" className="font-semibold text-gray-700">
                    Upload Complete
                  </Typography>
                  <Button variant="text" size="sm" onClick={() => setUploadLog([])}>
                    Dismiss
                  </Button>
                </div>
                <div className="space-y-1">
                  {uploadLog.map((log, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1">
                      <span className="text-gray-600 truncate max-w-[300px]">{log.name}</span>
                      {log.status === 'success' ? (
                        <span className="text-green-600">{log.count} rows uploaded</span>
                      ) : (
                        <span className="text-red-500">Error: {log.error}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Quick Filters */}
      {activeListings.length > 0 && (
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <Select
              isMulti
              options={filterOptions.contacts}
              value={filterContact}
              onChange={(v) => setFilterContact(v || [])}
              placeholder="Contact..."
              isClearable
              styles={selectStyles}
              menuPortalTarget={document.body}
              className="text-sm"
            />
          </div>
          <div className="flex-1 min-w-0">
            <Select
              isMulti
              options={filterOptions.manufacturers}
              value={filterManufacturer}
              onChange={(v) => setFilterManufacturer(v || [])}
              placeholder="Manufacturer..."
              isClearable
              styles={selectStyles}
              menuPortalTarget={document.body}
              className="text-sm"
            />
          </div>
          <div className="flex-1 min-w-0">
            <Select
              isMulti
              options={filterOptions.models}
              value={filterModel}
              onChange={(v) => setFilterModel(v || [])}
              placeholder="Model..."
              isClearable
              styles={selectStyles}
              menuPortalTarget={document.body}
              className="text-sm"
            />
          </div>
          {hasActiveFilters && (
            <button
              onClick={() => { setFilterContact([]); setFilterManufacturer([]); setFilterModel([]); }}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 whitespace-nowrap px-2 py-1 rounded border border-gray-200 hover:border-red-200 transition-colors"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Summary Cards */}
      {summaryStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
          {[
            { label: 'Listings', value: summaryStats.count },
            { label: 'Total Views', value: formatNum(summaryStats.totalViews) },
            { label: 'Total Impressions', value: formatNum(summaryStats.totalImpressions) },
            { label: 'Avg Daily Views', value: formatDec(summaryStats.avgDailyViews) },
            { label: 'Avg CTR', value: formatPct(summaryStats.avgCTR) },
            { label: 'Watchlists', value: formatNum(summaryStats.totalWatchlists) },
            { label: 'Email Leads', value: summaryStats.totalLeads },
          ].map((stat) => (
            <Card key={stat.label} className="shadow-sm">
              <CardBody className="p-3 text-center">
                <Typography variant="small" className="text-gray-500 text-xs">
                  {stat.label}
                </Typography>
                <Typography variant="h6" color="blue-gray" className="text-lg">
                  {stat.value}
                </Typography>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs + Group By */}
      <div className="flex items-center gap-4 mb-4">
        <Tabs value={activeTab} className="flex-1">
          <TabsHeader className="bg-gray-100 p-1" indicatorProps={{ className: 'bg-white shadow' }}>
            <Tab value="live" onClick={() => setActiveTab('live')}>
              Live Listings ({liveListings.length})
            </Tab>
            <Tab value="sold" onClick={() => setActiveTab('sold')}>
              Sold / Expired ({soldListings.length})
            </Tab>
            <Tab value="all" onClick={() => setActiveTab('all')}>
              All ({filteredListings.length})
            </Tab>
          </TabsHeader>
        </Tabs>
        <div className="flex items-center gap-2 shrink-0">
          <Typography variant="small" className="text-gray-500 text-xs whitespace-nowrap">
            Group by
          </Typography>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500 bg-white"
          >
            <option value="none">None</option>
            <option value="category">Category</option>
            <option value="manufacturer">Manufacturer</option>
            <option value="model">Model</option>
            <option value="contact">Contact</option>
          </select>
        </div>
      </div>

      {/* Comparison Charts (uses tab-filtered data) */}
      <WebStatsCharts
        filteredListings={activeTab === 'live' ? liveListings : activeTab === 'sold' ? soldListings : filteredListings}
        hasActiveFilters={hasActiveFilters}
        modelBenchmarks={modelBenchmarks}
      />

      {/* Grid */}
      <div className="flex-1 min-h-0">
        {activeListings.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ArrowUpTrayIcon className="h-12 w-12 mb-3" />
            <Typography variant="h6" className="text-gray-500 mb-1">No data for this date</Typography>
            <Typography variant="small">Upload Controller CSV files to get started</Typography>
          </div>
        ) : (
          <div className="ag-theme-quartz h-full">
            <AgGridReact
              rowData={getRowData()}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              pagination={groupBy === 'none'}
              paginationPageSize={50}
              getRowId={(params) => params.data._id}
              domLayout="normal"
              onRowClicked={onRowClicked}
              rowStyle={{ cursor: 'pointer' }}
              isFullWidthRow={isFullWidthRow}
              fullWidthCellRenderer={fullWidthCellRenderer}
            />
          </div>
        )}
      </div>

      {/* Listing detail dialog */}
      <WebStatsListingDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setSelectedListing(null); }}
        listing={selectedListing}
        loadWeeklyData={loadWeeklyData}
        peerListings={peerListings}
      />

      {/* Overwrite confirmation dialog */}
      {showOverwriteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30">
          <Card className="max-w-md shadow-xl">
            <CardBody className="p-6">
              <Typography variant="h6" color="blue-gray" className="mb-2">
                Overwrite existing data?
              </Typography>
              <Typography variant="small" className="text-gray-600 mb-1">
                The following weeks already have data uploaded:
              </Typography>
              <div className="my-3 space-y-1">
                {uploadQueue.filter((f) => f.existing && f.rows.length > 0).map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                    <span className="text-amber-800">{f.fileDt?.replace(/_/g, '/')}</span>
                    <span className="text-amber-600 text-xs">{f.count} rows</span>
                  </div>
                ))}
              </div>
              <Typography variant="small" className="text-gray-500 mb-4">
                Uploading will overwrite the existing data for these weeks.
              </Typography>
              <div className="flex justify-end gap-2">
                <Button variant="text" size="sm" onClick={() => setShowOverwriteConfirm(false)}>
                  Cancel
                </Button>
                <Button size="sm" color="amber" onClick={executeUpload}>
                  Overwrite & Upload
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}

export default WebStatsPage;
